import bs58 from 'bs58'
import { Provider } from '@coral-xyz/anchor'
// import NodeWallet from '@coral-xyz/anchor/dist/cjs/nodewallet'
import {
  BlockhashWithExpiryBlockHeight,
  ComputeBudgetProgram,
  Finality,
  // Keypair,
  PublicKey,
  // SignatureResult,
  SignatureStatus,
  Signer,
  TransactionInstruction,
  TransactionMessage,
  TransactionSignature,
  VersionedTransaction
} from '@solana/web3.js'

// import ENV_PARAMS from '@/constants/envParams'
import {
  DEFAULT_COMPUTE_UNIT_LIMIT
  // DEFAULT_RPC_ENDPOINT
} from '@/constants/settings'

import { solanaProvider } from './lib/providerFactory'

// export const connection = new Connection(DEFAULT_RPC_ENDPOINT.url, {
//   commitment: 'confirmed',
//   wsEndpoint: DEFAULT_RPC_ENDPOINT.ws
// })

// export const publicWallet = new NodeWallet(Keypair.generate())

// export const publicProvider = new AnchorProvider(connection, publicWallet, {
//   commitment: 'finalized'
// })

export async function getTokenMetadata(mintAddress: string) {
  const METADATA_PROGRAM_ID = new PublicKey(
    'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s'
  )
  const metadataAccount = PublicKey.findProgramAddressSync(
    [
      Buffer.from('metadata'),
      METADATA_PROGRAM_ID.toBuffer(),
      new PublicKey(mintAddress).toBuffer()
    ],
    METADATA_PROGRAM_ID
  )[0]
  const accountInfo =
    await solanaProvider.connection.getAccountInfo(metadataAccount)
  if (!accountInfo) throw new Error('Metadata account not found')
  // 解析二进制数据
  const data = accountInfo.data
  let offset = 0
  // 跳过 Key (1 byte)
  offset += 1
  // 跳过 Update Authority (32 bytes)
  offset += 32
  // 跳过 Mint Address (32 bytes)
  offset += 32
  // 读取 Data 结构
  // Data 结构包含：Name、Symbol、URI 等字段
  const nameLength = data.readUInt32LE(offset)
  offset += 4
  // const name = data.subarray(offset, offset + nameLength).toString();
  offset += nameLength
  const symbolLength = data.readUInt32LE(offset)
  offset += 4
  // const symbol = data.subarray(offset, offset + symbolLength).toString();
  offset += symbolLength
  const uriLength = data.readUInt32LE(offset)
  offset += 4
  const uri = data.subarray(offset, offset + uriLength).toString()
  // 转换 IPFS URI 为 HTTP 链接
  const ipfsUrl = uri.replace('ipfs://', 'https://ipfs.io/ipfs/')
  // 获取 IPFS 数据
  const metadata = await fetch(ipfsUrl).then((response) => response.json())
  const result = {
    uri: uri,
    ...metadata
  }
  return result
}

// 查询token创建时间（适合查询近期创建的token，交易量大的token不适用）
export async function getTokenCreationTime(
  mintAddress: string
): Promise<number | undefined> {
  const connection = solanaProvider.connection
  const mintPubkey = new PublicKey(mintAddress)
  let before = undefined
  let oldestSignature = null
  let blockTime
  try {
    while (true) {
      const signatures = await connection.getSignaturesForAddress(mintPubkey, {
        limit: 1000,
        before: before
      })
      if (signatures.length === 0) break
      oldestSignature = signatures[signatures.length - 1].signature
      before = oldestSignature
      if (signatures.length < 1000) break
    }
    if (oldestSignature) {
      const tx = await connection.getTransaction(oldestSignature, {
        commitment: 'confirmed',
        maxSupportedTransactionVersion: 0
      })
      blockTime = tx?.blockTime || undefined
    }
  } catch (error) {
    console.error(error)
  }
  return blockTime
}

// 获取持仓数前20账号
export async function getLargestTokenAccounts(
  mintAddress: string
): Promise<any[]> {
  const accountsResp = await solanaProvider.connection.getTokenLargestAccounts(
    new PublicKey(mintAddress)
  )
  const accounts: any[] = accountsResp.value
  const accountAddress = accounts.map((item) => item.address)
  const holders =
    await solanaProvider.connection.getMultipleAccountsInfo(accountAddress)
  holders.forEach((holder, index) => {
    const account = accounts[index]
    if (holder && account) {
      account.executable = holder.executable
      account.address = account.address?.toBase58()
      account.mint = new PublicKey(holder.data.slice(0, 32)).toBase58()
      account.owner = new PublicKey(holder.data.slice(32, 64)).toBase58()
    }
  })
  return accounts
}

export function deserializeVersionedTx(base58Tx: string): VersionedTransaction {
  return VersionedTransaction.deserialize(bs58.decode(base58Tx))
}

/**
 * 打包交易指令
 * @param provider
 * @param instructions
 * @param priorityFee
 * @param signers
 * @param tableAccounts
 * @returns
 */
export async function packageTransaction(
  provider: Provider,
  instructions: TransactionInstruction[],
  signers?: Signer[] | undefined,
  options?: {
    priorityFee?: number
    tableAccounts?: string[]
    isModifyComputeUnits?: boolean
  }
): Promise<{
  transaction: VersionedTransaction
  latestBlockhash: BlockhashWithExpiryBlockHeight
}> {
  if (!provider) {
    throw new Error('Unknow wallet provider')
  }
  const tradeProvider = provider
  const arrInst = instructions
  if (arrInst.length === 0) {
    throw new Error('Transaction instructions is empty')
  }
  const {
    priorityFee,
    tableAccounts,
    isModifyComputeUnits = false
  } = options || {}
  const addressLookupTables: any[] = []
  // const allAltAddr: PublicKey[] = [
  //   new PublicKey(ENV_PARAMS.PLATFORM_LOOKUP_TABLE)
  // ]
  if (tableAccounts?.length) {
    for (const accountKey of tableAccounts) {
      const respTable = await tradeProvider.connection
        .getAddressLookupTable(new PublicKey(accountKey))
        .catch(() => null)
      if (respTable?.value) {
        addressLookupTables.push(respTable?.value)
      }
    }
  }

  const latestBlockhash = await tradeProvider.connection.getLatestBlockhash()
  if (isModifyComputeUnits || priorityFee) {
    const modifyComputeUnits = ComputeBudgetProgram.setComputeUnitLimit({
      units: DEFAULT_COMPUTE_UNIT_LIMIT
    })
    arrInst.unshift(modifyComputeUnits)
  }
  if (priorityFee) {
    const addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: priorityFee
    })
    arrInst.unshift(addPriorityFee)
  }

  const transaction = new VersionedTransaction(
    new TransactionMessage({
      payerKey: tradeProvider.publicKey!,
      recentBlockhash: latestBlockhash.blockhash,
      instructions: arrInst
    }).compileToV0Message(addressLookupTables)
  )
  if (signers?.length) {
    transaction.sign(signers)
  }

  return {
    transaction,
    latestBlockhash
  }
}

/**
 * 查询交易确认状态
 * @param txHash
 * @param opts
 * @returns
 */
export async function confirmTransactionStatus(
  txHashes: TransactionSignature[],
  opts?: {
    latestBlockhash?: BlockhashWithExpiryBlockHeight
    finality?: Finality
    timeout?: number
    interval?: number
  }
): Promise<SignatureStatus[]> {
  const {
    latestBlockhash,
    finality = 'confirmed',
    timeout = finality === 'finalized' ? 120000 : 60000,
    interval = 3000
  } = opts || {}

  const statusLevels = ['confirmed', 'finalized']
  const minIndex = statusLevels.findIndex((s) => s === finality)
  const startTime = Date.now()
  const results: Record<string, SignatureStatus | null> = Object.fromEntries(
    txHashes.map((h) => [h, null])
  )
  let blockHeight = latestBlockhash?.lastValidBlockHeight
  let count = 0

  while (Date.now() - startTime < timeout) {
    count++

    const { value: statuses } =
      await solanaProvider.connection.getSignatureStatuses(txHashes, {
        searchTransactionHistory: blockHeight ? false : true
      })

    for (let i = 0; i < txHashes.length; i++) {
      const hash = txHashes[i]
      const value = statuses[i]
      if (results[hash]) continue // 已确认，跳过
      if (value?.err) {
        throw {
          message: `Transaction ${hash} failed`,
          timeout: false,
          data: value
        }
      }

      if (value?.confirmationStatus) {
        const currentIndex = statusLevels.findIndex(
          (s) => s === value.confirmationStatus
        )
        if (currentIndex >= minIndex) {
          results[hash] = value
        }
      }
    }

    const stillPending = Object.values(results).some((v) => v === null)
    if (!stillPending) {
      return Object.values(results) as SignatureStatus[]
    }

    // 检查 blockHeight 是否过期
    if (blockHeight && count % 5 === 0) {
      const currentHeight = await solanaProvider.connection.getBlockHeight()
      if (currentHeight > blockHeight) {
        blockHeight = undefined
      }
    }

    await new Promise((resolve) => setTimeout(resolve, interval))
  }

  throw {
    message: 'Transaction confirm timeout',
    timeout: true,
    data: results
  }
}

// export async function subscribeTxStatus(
//   txHashes: TransactionSignature[],
//   opts?: {
//     finality?: Finality
//     timeout?: number
//   }
// ) {
//   const {
//     finality = 'confirmed',
//     timeout = 60000,
//   } = opts || {}

//   const subIds: number[] = []
//   const results: SignatureResult[] = new Array(txHashes.length)
//   const fnSub = (txHash: TransactionSignature, index: number) => {
//     return new Promise((resolve, reject) => {
//       const sid = solanaProvider.connection.onSignature(
//         txHash,
//         (result, context) => {
//           console.log('Transaction status:', result, context)
//           solanaProvider.connection.removeSignatureListener(sid)
//           resolve(result)
//           results[index] = result
//         },
//         finality
//       );
//       subIds.push(sid)
//     })
//   }

//   const timer = setTimeout(() => {
//     subIds.forEach((sid) => {
//       solanaProvider.connection.removeSignatureListener(sid)
//     })
//     throw {
//       message: 'Transaction confirm timeout',
//       timeout: true,
//       data: results
//     }
//   }, timeout)

//   const promiseArr = txHashes.map((txHash, index) => {
//     return fnSub(txHash, index)
//   })
//   await Promise.allSettled(promiseArr)
//   clearTimeout(timer)
//   console.log('results >>>>>>>>>', results)
//   return results
// }

// const funcGetStatus = () => {
// return connection.getSignatureStatus(txHash, {
//   searchTransactionHistory: true,
// }).then((resp) => {
//   const { value } = resp;
//   if (!value) {
//     return null;
//   }
//   else if (value.err) {
//     return Promise.reject({
//       message: 'Transaction failed',
//       timeout: false,
//       data: value
//     });
//   } else if (value.confirmationStatus) {
//     const currentIndex = statusLevels.findIndex(s => s === value.confirmationStatus);
//     if (currentIndex >= minIndex) {
//       return value;
//     }
//   }
// })

// export async function getTxDetails(
//   connection: Connection,
//   sig: string,
//   commitment: Commitment = DEFAULT_COMMITMENT,
//   finality: Finality = DEFAULT_FINALITY,
//   opt?: BlockhashWithExpiryBlockHeight
// ): Promise<VersionedTransactionResponse | null> {
//   if (!opt?.blockhash) {
//     opt = await connection.getLatestBlockhash();
//   }
//   await connection.confirmTransaction(
//     {
//       blockhash: opt.blockhash,
//       lastValidBlockHeight: opt.lastValidBlockHeight,
//       signature: sig,
//     },
//     commitment
//   );

//   return connection.getTransaction(sig, {
//     maxSupportedTransactionVersion: 0,
//     commitment: finality,
//   });
// }

// export async function sendAndConfirmTransaction(
//   connection: Connection,
//   tx: Transaction | VersionedTransaction,
//   options?: {
//     timeout?: number;
//     interval?: number;
//     sendOptions?: SendOptions;
//     minStatus?: Commitment;
//     maxRetries?: number;
//   }
// ): Promise<TransactionSignature> {
//   const defaultOptions = {
//     timeout: 60000,
//     interval: 2000,
//     sendOptions: {
//       skipPreflight: false,
//       preflightCommitment: 'confirmed'
//     },
//     minStatus: 'confirmed',
//     maxRetries: 3,
//   }
//   const {
//     timeout,
//     interval,
//     sendOptions,
//     minStatus,
//     maxRetries,
//   } = { ...defaultOptions, ...options };

//   const rawTx = tx.serialize();
//   let txHash: TransactionSignature | null = null;
//   let retries = 0;

//   const statusLevels = ['processed', 'confirmed', 'finalized'];
//   const requiredIndex = statusLevels.indexOf(minStatus!);

//   while (retries < maxRetries) {
//     try {
//       txHash = await connection.sendRawTransaction(rawTx, sendOptions as SendOptions);

//       const start = Date.now();
//       while (Date.now() - start < timeout) {
//         const res = await connection.getSignatureStatus(txHash, {
//           searchTransactionHistory: true,
//         });
//         const status = res.value;
//         if (status?.err) {
//           throw new Error(`Transaction failed: ${JSON.stringify(status.err)}`);
//         }
//         const confirmation = status?.confirmationStatus;
//         if (confirmation) {
//           const currentIndex = statusLevels.indexOf(confirmation);
//           if (currentIndex >= requiredIndex) {
//             return txHash;
//           }
//         }
//         await new Promise((r) => setTimeout(r, interval));
//       }

//       throw new Error(`Timeout waiting for confirmation of tx ${txHash}`);
//     } catch (err) {
//       console.warn(`Retry ${retries + 1} failed:`, err);
//       retries++;
//     }
//   }

//   throw new Error(`Failed to send and confirm transaction after ${maxRetries} retries`);
// }

// /**
//  * 查询交易确认状态
//  * @param txHash
//  * @param opts
//  * @returns
//  */
// export async function getTransactionStatus(
//   txHash: string,
//   opts?: {
//     latestBlockhash?: BlockhashWithExpiryBlockHeight
//     finality?: Finality
//     timeout?: number
//     interval?: number
//   }
// ): Promise<SignatureStatus> {
//   const {
//     latestBlockhash,
//     finality = 'finalized',
//     timeout = 60000,
//     interval = 3000
//   } = opts || {}

//   const statusLevels = ['confirmed', 'finalized']
//   const minIndex = statusLevels.findIndex((s) => s === finality)
//   const startTime = Date.now()
//   let errorMsg

//   // If blockhash is provided, use official confirmTransaction
//   if (latestBlockhash?.blockhash) {
//     try {
//       console.log('connection.confirmTransaction', finality, latestBlockhash)
//       await connection.confirmTransaction(
//         {
//           blockhash: latestBlockhash.blockhash,
//           lastValidBlockHeight: latestBlockhash.lastValidBlockHeight,
//           signature: txHash
//         },
//         finality
//       )
//       // console.log('getTransaction', finality, aaa)
//       // const aaa = await connection.getTransaction(txHash, {
//       //   commitment: finality,
//       //   maxSupportedTransactionVersion: 0,
//       // });
//       // const { value } = await connection.getSignatureStatus(txHash, {
//       //   searchTransactionHistory: true,
//       // })
//       // console.log('confirmTransaction =====', aaa)
//       return {
//         confirmationStatus: finality
//       } as SignatureStatus
//     } catch (err: any) {
//       console.log('err', err)
//       errorMsg = err?.message
//       // if (
//       //   err.name === 'TransactionExpiredBlockheightExceededError' ||
//       //   err.message.includes('has expired: block height exceeded')
//       // ) {
//       //   errorMsg = err?.message
//       // } else {
//       //   console.warn('confirmTransaction failed:', err);
//       // }
//     }
//   }

//   // Fallback: poll using getSignatureStatus (works even with old transactions)
//   while (Date.now() - startTime < timeout) {
//     const { value } = await connection.getSignatureStatus(txHash, {
//       searchTransactionHistory: true
//     })
//     console.log('getSignatureStatus =====', value)
//     if (value?.err) {
//       return Promise.reject({
//         message: 'Transaction failed',
//         timeout: false,
//         data: value
//       })
//     } else if (value?.confirmationStatus) {
//       const currentIndex = statusLevels.findIndex(
//         (s) => s === value.confirmationStatus
//       )
//       if (currentIndex >= minIndex) {
//         return value
//       }
//     }
//     await new Promise((resolve) => setTimeout(resolve, interval))
//   }

//   return Promise.reject({
//     message: errorMsg || 'Transaction status timeout',
//     timeout: true
//   })
// }

// export async function signAndSendTransaction(
//   provider: Provider,
//   base58Tx: string
// ): Promise<{
//   txHash: string
// }> {
//   if (!provider) {
//     throw new Error('Unknow wallet provider')
//   }
//   const versionedTx = VersionedTransaction.deserialize(bs58.decode(base58Tx))
//   // const decompileMessage = TransactionMessage.decompile(
//   //   versionedTx.message,
//   // );
//   // const tx = await packageTransaction(provider, decompileMessage.instructions)
//   // console.log('versionedTx======', versionedTx)
//   const txHash = await provider.sendAndConfirm!(versionedTx)
//   return { txHash }
// }

// export const getToken = async ()=>{
//   const supply = await connection.getTokenSupply(new PublicKey('9Jp8yJdqpkQ2XsRPrBHFqQnNuETvak3dZQdyLuNmpfot'))
//   // const bbb = await connection.getTokenAccountBalance(new PublicKey('9Jp8yJdqpkQ2XsRPrBHFqQnNuETvak3dZQdyLuNmpfot'))
//   // console.log('aaa', supply)
//   // console.log('bbb', bbb)
//   const tokenBalance = supply.value.uiAmount || 0;
//   const progress = new BigNumber(100).minus(BigNumber(tokenBalance).minus(972447326).multipliedBy(100).dividedBy(27552674)).toNumber();
//   console.log('progress===', progress)
// }

// /**
//  *
//  * @param provider 发送MEV防御交易
//  * @param tx
//  * @param opts
//  * @returns
//  */
// export async function sendMevProtectionTransaction(
//   provider: TradeProvider,
//   signedTx: Transaction | VersionedTransaction,
//   opts?: ConfirmOptions
// ): Promise<any> {
//   if (!provider) {
//     throw new Error('Unknow wallet provider');
//   }
//   if (!opts) {
//     opts = { "skipPreflight": false, "preflightCommitment": "finalized" }
//   }
//   const serializeTx = Buffer.from(signedTx.serialize()).toString('base64');
//   const endpoint = provider.connection.rpcEndpoint;
//   const payload = {
//     jsonrpc: "2.0",
//     id: 1,
//     method: "sendTransaction",
//     params: [serializeTx, { ...opts, "encoding": "base64" }],
//   };
//   const response = await fetcher(
//     endpoint, {
//     method: 'POST',
//     headers: {
//       "Content-Type": "application/json",
//     },
//     body: JSON.stringify(payload),
//   })
//   return response.result;
// }

// /**
//  * 异步轮询交易确认状态
//  * @param txHash
//  * @param txStatus
//  * @param timeout
//  * @param interval
//  * @returns
//  */
// export async function getTransactionStatus(
//   txHash: string,
//   txStatus: TransactionConfirmationStatus = 'finalized',
//   timeout: number = 120000,
//   interval: number = 3000
// ): Promise<any> {
//   const startTime = Date.now();
//   const statusLevels = ['processed', 'confirmed', 'finalized'];
//   const statusIndex = statusLevels.findIndex((item) => item === txStatus);
//   while (Date.now() - startTime < timeout) {
//     const { value } = await connection.getSignatureStatus(txHash, {
//       searchTransactionHistory: true,
//     });
//     if (value?.err) {
//       return Promise.reject({
//         message: 'Transaction fail',
//         timeout: false,
//         data: value
//       })
//     } else if (value?.confirmationStatus) {
//       const index = statusLevels.findIndex((item) => item === value.confirmationStatus);
//       if (index >= statusIndex) {
//         return value;
//       }
//     }
//     await new Promise(resolve => setTimeout(resolve, interval));
//   }
//   return Promise.reject({
//     message: 'Get transaction finalized timeout',
//     timeout: true
//   })
// }
